import * as THREE from 'three';
import { LUCAN } from '@somnhold/content';
import { simToWorld, WORLD_PER_HEX } from '../coords.ts';
import type { UnRender } from './ctx.ts';

/**
 * Prior Lucan's own effects: the souls he keeps circling him, his prayer rising off the nightmares it mends, the
 * shadow bolt, the whip's chain from hero to hero, a soul flying to him, the fallen raised, and the Empty Chapel —
 * pillars of violet light standing up round the circle, then a pale ring rolling out.
 */
const TAU = Math.PI * 2;
const GLOW = '#b07aff';
const ACCENT = '#9a5cff';
const PALE = '#e6d6ff';
const DARK = '#120c1a';
const SMOKE = '#1a1422';
const q = new THREE.Vector3();

export const lucanFx: UnRender = {
  look: { glow: GLOW, accent: ACCENT, pale: PALE, dark: DARK, smoke: SMOKE },
  clips: { lucanWhip: 'whip', lucanRaise: 'raise' },
  frame: (c, s, e, p, _yaw, h) => {
    const u = e.un;
    if (!u) return;
    // His souls circle him, one mote lit at a time on its way round.
    const souls = u.k[0] ?? 0;
    if (souls > 0 && s.tick % 2 === 0) {
      const i = (s.tick >> 1) % souls;
      const a = (i / souls) * TAU + s.tick * 0.08;
      c.vfx.burst(p.clone().add(q.set(Math.cos(a) * 0.75, h * 0.55 + Math.sin(a * 2) * 0.15, Math.sin(a) * 0.75)), { n: 1, color: PALE, speed: 0.004, up: 0.004, size: 0.11, life: 10, gravity: -0.002 });
    }
    // The prayer's beat: motes rise off each wounded nightmare near him.
    if ((u.k[1] ?? 1) === 0) {
      let n = 0;
      for (const o of s.enemies) {
        if (o === e || o.hp <= 0 || o.hp >= o.maxHp || o.kind === 'boneWall') continue;
        const dx = o.x - e.x;
        const dy = o.y - e.y;
        if (dx * dx + dy * dy > LUCAN.prayer.radius * LUCAN.prayer.radius) continue;
        const w = simToWorld(o.x, o.y, new THREE.Vector3(), p.y);
        c.vfx.burst(w.setY(w.y + 0.3), { n: c.low ? 2 : 4, color: GLOW, speed: 0.01, up: 0.04, size: 0.08, life: 14, gravity: -0.003, spread: 0.25 });
        if (++n >= (c.low ? 4 : 10)) break;
      }
      if (n > 0) c.vfx.ring(p.clone().setY(p.y + 0.05), 0.3, (LUCAN.prayer.radius / 1000) * WORLD_PER_HEX, ACCENT, 14, 0.25);
    }
  },
  beam: (c, kind, a, b) => {
    if (kind === 'lucanBolt') {
      c.vfx.beam(a.clone().setY(a.y + 0.5), b, ACCENT, 5, 0.06, 0.08);
      c.vfx.beam(a.clone().setY(a.y + 0.5), b, PALE, 3, 0.02, 0);
      c.vfx.burst(b, { n: c.low ? 3 : 7, color: GLOW, speed: 0.05, up: 0.04, size: 0.09, life: 9 });
      c.vfx.burst(b, { n: c.low ? 1 : 2, color: SMOKE, speed: 0.02, up: 0.02, size: 0.4, life: 12, smoke: true });
      return true;
    }
    if (kind === 'lucanWhip') {
      // A chain of shadow, link by link: jagged, and a curse ring where it bites.
      c.vfx.beam(a, b, ACCENT, 8, 0.05, 0.25);
      c.vfx.beam(a, b, PALE, 5, 0.018, 0.12);
      c.vfx.ring(b.clone().setY(b.y - 0.55), 0.15, 0.7, GLOW, 12, 0.9);
      c.flare(b, ACCENT, 1.2, 8);
      return true;
    }
    if (kind === 'lucanSoul') {
      // A soul flies to him: a pale thread, drawn in where it lands.
      c.vfx.beam(a, b, PALE, 6, 0.02, 0.15);
      c.vfx.implode(b.clone().setY(b.y + 0.3), 0.8, { n: c.low ? 2 : 5, color: PALE, size: 0.07, life: 10, swirl: 0.5, lift: 0.02 });
      return true;
    }
    return false;
  },
  boom: (c, _s, e, kind, p, r) => {
    const low = c.low;
    if (kind === 'lucanRaise') {
      c.vfx.ring(p.clone().setY(p.y + 0.05), 0.2, r, ACCENT, 16, 1);
      c.vfx.burst(p.clone().setY(p.y + 0.3), { n: low ? 6 : 16, color: DARK, speed: 0.05, up: 0.06, size: 0.7, life: 24, smoke: true, spread: 0.5 });
      c.vfx.burst(p.clone().setY(p.y + 0.2), { n: low ? 6 : 16, color: GLOW, speed: 0.07, up: 0.12, size: 0.1, life: 18, gravity: 0.004, spread: 0.5 });
      c.flare(p.clone().setY(p.y + 1.4), ACCENT, 2.5, 12);
      c.shake(0.2);
      return true;
    }
    if (kind !== 'lucanChapel') return false;
    // The Empty Chapel: pillars of violet light round the circle, a pale ring rolling out, smoke and souls in it.
    if (e) c.force(e.id, 'chapel');
    const R = (LUCAN.chapel.radius / 1000) * WORLD_PER_HEX;
    const n = low ? 6 : 10;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU;
      const base = p.clone().add(q.set(Math.cos(a) * R, 0.05, Math.sin(a) * R));
      c.vfx.shaft(base, base.clone().setY(base.y + 3.2), ACCENT, 18, 0.35);
      c.vfx.burst(base.clone().setY(base.y + 0.2), { n: low ? 2 : 4, color: SMOKE, speed: 0.03, up: 0.05, size: 0.8, life: 24, smoke: true, spread: 0.3 });
    }
    c.vfx.shaft(p, p.clone().setY(p.y + 6), PALE, 16, 0.8);
    c.vfx.ring(p.clone().setY(p.y + 0.06), 0.3, R, PALE, 16, 1);
    c.vfx.ring(p.clone().setY(p.y + 0.1), 0.2, R * 0.7, ACCENT, 20, 0.8);
    c.vfx.burst(p.clone().setY(p.y + 0.6), { n: low ? 10 : 26, color: GLOW, speed: 0.1, up: 0.1, size: 0.11, life: 20, gravity: 0.003, spread: 0.6 });
    c.flare(p.clone().setY(p.y + 1.2), ACCENT, 7, 16);
    c.shake(0.8);
    return true;
  },
};
